import React, {useState} from "react";
import {
    disableSecondFactor,
    issueSecondFactor,
    SECOND_FACTOR_DEFAULT_SESSION_DAYS,
    SECOND_FACTOR_MAX_SESSION_DAYS,
    setSecondFactorSessionDays,
} from "../api";
import type {SecondFactorEnrollment, SecondFactorStatus} from "../api";
import {useAuth} from "../auth";
import {formatDate, groupSecondFactorSecret} from "../utils";

import {ErrorBanner} from "./ErrorBanner";
import {Modal} from "./Modal";

interface SecondFactorControlProps {
    /** Пользователь, которому включают 2FA. Нет — управлять нечем (например, доступ ещё не выдан). */
    userId?: string;
    login: string;
    status?: SecondFactorStatus;
    onChanged: (status: SecondFactorStatus) => void;
}

type Pending = "issue" | "disable" | "days" | null;


/** Целое число суток в допустимых границах; null, пока поле заполнено неверно. */
function parseSessionDays(raw: string): number | null {
    if (!/^\d+$/.test(raw.trim())) return null;
    const days = Number(raw);
    return days >= 1 && days <= SECOND_FACTOR_MAX_SESSION_DAYS ? days : null;
}

/**
 * Двухфакторный вход одного пользователя: статус, срок входа и кнопки «Включить» /
 * «Выпустить заново» / «Выключить». QR и ключ показываются один раз, сразу
 * после выпуска; админ передаёт их человеку сам.
 */
export function SecondFactorControl({userId, login, status, onChanged}: SecondFactorControlProps): JSX.Element {
    const {token} = useAuth();
    const enabled = Boolean(status?.enabled);
    const currentDays = status?.sessionDays ?? SECOND_FACTOR_DEFAULT_SESSION_DAYS;
    const [pending, setPending] = useState<Pending>(null);
    const [daysInput, setDaysInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const [enrollment, setEnrollment] = useState<SecondFactorEnrollment | null>(null);
    const [copied, setCopied] = useState(false);

    if (!userId) {
        return <span className="cell-subtle">—</span>;
    }

    const days = parseSessionDays(daysInput);
    const asksDays = pending === "issue" || pending === "days";

    const open = (next: Pending): void => {
        setDaysInput(String(currentDays));
        setError(null);
        setPending(next);
    };

    const close = (): void => {
        if (busy) return;
        setPending(null);
        setError(null);
    };

    const confirm = async (): Promise<void> => {
        if (asksDays && days === null) return;
        setBusy(true);
        setError(null);
        try {
            if (pending === "issue" && days !== null) {
                const issued = await issueSecondFactor(token, userId, days);
                setEnrollment(issued);
                setCopied(false);
                onChanged({enabled: true, issuedAt: issued.issuedAt, sessionDays: issued.sessionDays});
            } else if (pending === "days" && days !== null) {
                onChanged(await setSecondFactorSessionDays(token, userId, days));
            } else if (pending === "disable") {
                onChanged(await disableSecondFactor(token, userId));
            }
            setPending(null);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    const copySecret = async (): Promise<void> => {
        if (!enrollment) return;
        try {
            await navigator.clipboard.writeText(enrollment.secret);
            setCopied(true);
        } catch {
            setCopied(false);
        }
    };

    const title =
        pending === "disable"
            ? `Выключить 2FA для ${login}`
            : pending === "days"
              ? `Срок входа для ${login}`
              : `${enabled ? "Выпустить заново" : "Включить"} 2FA для ${login}`;

    const confirmLabel =
        pending === "disable" ? "Выключить" : pending === "days" ? "Сохранить" : enabled ? "Выпустить" : "Включить";

    return (
        <div className="second-factor-control">
            <span
                className="second-factor-status"
                title={status?.issuedAt ? `Выпущена ${formatDate(status.issuedAt)}` : undefined}
            >
                <span className={`status-dot ${enabled ? "dot-active" : "dot-inactive"}`} />
                {enabled ? "2FA включена" : "2FA выключена"}
                {enabled && (
                    <>
                        <span className="second-factor-days"> · вход {currentDays} сут.</span>
                        <button className="second-factor-days-edit" type="button" onClick={() => open("days")}>
                            Изменить
                        </button>
                    </>
                )}
            </span>
            <div className="second-factor-actions">
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => open("issue")}>
                    {enabled ? "Выпустить заново" : "Включить"}
                </button>
                {enabled && (
                    <button className="btn btn-ghost btn-sm" type="button" onClick={() => open("disable")}>
                        Выключить
                    </button>
                )}
            </div>

            <Modal open={pending !== null} title={title} onClose={close} disableClose={busy} sizeClassName="modal-sm">
                {pending === "disable" && <p className="modal-hint">Дальше {login} входит только по паролю, без 2FA.</p>}
                {pending === "issue" && (
                    <p className="modal-hint">
                        {enabled ? "Старый QR-код перестанет работать. " : ""}
                        Все открытые сессии {login} закроются; дальше вход — по паролю и 2FA. Когда срок входа
                        кончится, {login} сам снова вводит пароль и шесть цифр из того же приложения.
                    </p>
                )}
                {pending === "days" && (
                    <p className="modal-hint">
                        Новый срок — со следующего входа {login}. QR-код прежний, открытые сессии не закрываются.
                    </p>
                )}
                {asksDays && (
                    <div className="form-group second-factor-days-field">
                        <label htmlFor={`second-factor-days-${userId}`}>Вход действует, суток</label>
                        <input
                            id={`second-factor-days-${userId}`}
                            type="number"
                            inputMode="numeric"
                            min={1}
                            max={SECOND_FACTOR_MAX_SESSION_DAYS}
                            value={daysInput}
                            onChange={(e) => setDaysInput(e.target.value)}
                            disabled={busy}
                        />
                        <div className={days === null ? "second-factor-days-hint is-error" : "second-factor-days-hint"}>
                            От 1 до {SECOND_FACTOR_MAX_SESSION_DAYS}, обычно {SECOND_FACTOR_DEFAULT_SESSION_DAYS}.
                        </div>
                    </div>
                )}
                <ErrorBanner error={error} />
                <div className="modal-actions">
                    <button className="btn btn-ghost" type="button" onClick={close} disabled={busy}>
                        Отмена
                    </button>
                    <button
                        className={`btn ${pending === "disable" ? "btn-danger" : "btn-primary"}`}
                        type="button"
                        onClick={() => void confirm()}
                        disabled={busy || (asksDays && days === null)}
                    >
                        {busy ? "…" : confirmLabel}
                    </button>
                </div>
            </Modal>

            <Modal
                open={enrollment !== null}
                title={`2FA для ${enrollment?.login ?? login}`}
                onClose={() => setEnrollment(null)}
            >
                {enrollment && (
                    <>
                        <div className="alert alert-warning">
                            QR-код показывается только сейчас. Не отправляйте его в тот же чат, где пароль.
                            Попросите отсканировать его в Google Authenticator, Authy или другом приложении
                            для 2FA, а после этого удалите сообщение.
                        </div>
                        <div className="second-factor-qr">
                            <img src={enrollment.qrCode} alt={`QR-код для ${enrollment.login}`} />
                        </div>
                        <p className="modal-hint">Если сканировать неудобно — ключ для ручного ввода:</p>
                        <div className="second-factor-secret mono">{groupSecondFactorSecret(enrollment.secret)}</div>
                        <div className="modal-actions">
                            <button className="btn btn-ghost" type="button" onClick={() => void copySecret()}>
                                {copied ? "Скопировано" : "Копировать ключ"}
                            </button>
                            <button className="btn btn-primary" type="button" onClick={() => setEnrollment(null)}>
                                Готово
                            </button>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
}
